import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from '@/constants/routes';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { useApiRequest } from '@/hooks/useApiRequest';

export const PasswordSettingConfirm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { translateApiError } = useApiErrorTranslation();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // location.stateからemailを取得（前画面から渡される想定）
  const email = (location.state as { email?: string })?.email;

  const { isRequesting, execute } = useApiRequest<'POST', 'PasswordResetEmail'>(
    'POST',
    'PasswordResetEmail',
  );

  // 遷移元でデータがセットされていない場合は想定フローではないのでログイン画面に戻す
  useEffect(() => {
    if (!email) {
      navigate(ROUTE_PATH.LOGIN);
    }
  }, [email, navigate]);
  if (!email) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isConfirmed) {
      setErrorMessage('');

      const result = await execute({ email });

      if (result.ok) {
        navigate(ROUTE_PATH.PASSWORD_RESET_SENT);
      } else {
        const { message } = translateApiError(result.error);
        setErrorMessage(message);
      }
    }
  };

  return (
    <div className="page p-auth page_a2">
      <main className="page__main">
        <div className="page__content">
          <section className="l-section">
            <div className="l-section__line">
              <div className="l-container">
                <div className="l-hero">
                  <img
                    className="l-hero__img"
                    src="/Common_Title.jpg"
                    alt="LOUNGE RESERVATION"
                  />
                </div>

                <div className="l-grid">
                  <section className="l-card">
                    <h2 className="l-card__title">
                      {t('passwordSettingConfirm.title')}
                    </h2>
                    <div className="l-card__body">
                      <div className="l-text-group l-text-group--white l-pt0">
                        <p className="l-text">
                          <Trans
                            i18nKey="passwordSettingConfirm.description"
                            components={{ br: <br /> }}
                          />
                        </p>
                      </div>
                      <div className="l-media l-mb20">
                        <img
                          className="l-media__img"
                          src="/Common_Related_Services.png"
                          alt="workhub"
                        />
                      </div>
                      <div className="l-notice-box l-mb30">
                        <div className="l-notice-box__inner">
                          <div className="l-notice-box__icon">
                            <img
                              src="/Common_Icon_Note.png"
                              alt={t('common.altText.attention')}
                              className="l-notice-box__icon-img"
                            />
                          </div>
                          <div className="l-notice-box__text">
                            {t('passwordSettingConfirm.notice1')}
                          </div>
                        </div>
                      </div>
                      <div className="password-change">
                        <form
                          className="password-change__form"
                          onSubmit={handleSubmit}
                        >
                          <div className="password-change__email">
                            <div className="password-change__email-label">
                              {t('passwordSettingConfirm.emailLabel')}
                            </div>
                            <div className="password-change__email-value">
                              {email}
                            </div>
                          </div>
                          <div className="password-change">
                            <label className="password-change__confirm">
                              <input
                                type="checkbox"
                                className="password-change__confirm-checkbox"
                                checked={isConfirmed}
                                onChange={(e) =>
                                  setIsConfirmed(e.target.checked)
                                }
                              />
                              <span className="password-change__confirm-label">
                                {t('passwordSettingConfirm.confirmCheckbox')}
                              </span>
                            </label>
                          </div>
                          <button
                            className={`password-change__submit c-auth__btn c-auth__btn--next ${!isConfirmed ? 'is-disabled' : ''}`}
                            type="submit"
                            disabled={!isConfirmed || isRequesting}
                          >
                            {t('common.btn.next')}
                          </button>
                        </form>
                      </div>
                      {errorMessage && (
                        <div className="c-auth__alerts">
                          <p className="c-auth__alert c-auth__alert--error">
                            {errorMessage}
                          </p>
                        </div>
                      )}
                      <div className="l-notice-box">
                        <div className="l-notice-box__inner">
                          <div className="l-notice-box__icon">
                            <img
                              src="/Common_Icon_Note.png"
                              alt={t('common.altText.attention')}
                              className="l-notice-box__icon-img"
                            />
                          </div>
                          <div className="l-notice-box__text">
                            {t('passwordSettingConfirm.notice2')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};
